import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-2xl bg-white border border-slate-200 px-4 py-3.5 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 shadow-sm";

const GENDER_OPTIONS = [
  ["female", "Female"],
  ["male", "Male"],
  ["non_binary", "Non-binary"],
  ["other", "Other"],
  ["prefer_not_to_say", "Prefer not to say"],
];

const SOURCE_OF_FUNDS_OPTIONS = [
  ["salary", "Salary or wages"],
  ["business_income", "Business"],
  ["investments", "Investments"],
];

  // ["inheritance", "Inheritance"],
  // ["pension", "Pension"],
  // ["family_support", "Family support"],
  // ["sale_of_assets", "Sale of assets"],
  // ["other", "Other"],

export function DetailsStep({ onNext, onBack, loading, error }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch("password");
  const pin = watch("pin");

  const onSubmit = async (data) => {
    try {
      if (!onNext) {
        return;
      }
      await onNext(data);
    } catch {}
  };

  return (
    <div className="flex flex-col h-full w-full max-w-xl mx-auto bg-[#FDFDFD] p-5 md:p-8 text-left">
      <button
        onClick={onBack}
        className="mb-6 w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30 shadow-sm"
      >
        <ChevronLeft size={20} />
      </button>

      <div className="mb-8 text-center">
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold tracking-tight">
          Sign Up
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 flex-grow">
        {/* Legal name — collected separately so payout providers receive both parts. */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel>First name</FieldLabel>
              <input
                {...register("firstName", {
                  required: "First name is required",
                  validate: (value) =>
                    value.trim().length > 0 || "First name is required",
                })}
                autoComplete="given-name"
                placeholder="ex: Jon"
                className={INPUT_CLASS}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div>
              <FieldLabel>Last name</FieldLabel>
              <input
                {...register("lastName", {
                  required: "Last name is required",
                  validate: (value) =>
                    value.trim().length > 0 || "Last name is required",
                })}
                autoComplete="family-name"
                placeholder="ex: Smith"
                className={INPUT_CLASS}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Profile and compliance information */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
          <div>
            <FieldLabel>Gender</FieldLabel>
            <select
              {...register("gender", { required: "Select a gender option" })}
              defaultValue=""
              className={INPUT_CLASS}
            >
              <option value="" disabled>
                Select an option
              </option>
              {GENDER_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.gender && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.gender.message}
              </p>
            )}
          </div>

          <div>
            <FieldLabel>Primary source of funds</FieldLabel>
            <select
              {...register("sourceOfFunds", {
                required: "Select your primary source of funds",
              })}
              defaultValue=""
              className={INPUT_CLASS}
            >
              <option value="" disabled>
                Select an option
              </option>
              {SOURCE_OF_FUNDS_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.sourceOfFunds && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.sourceOfFunds.message}
              </p>
            )}
            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
              We use this information for compliance and transaction monitoring.
            </p>
          </div>
        </div>

        {/* Transaction PIN */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
          <div>
            <FieldLabel>6-digit transaction PIN</FieldLabel>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              {...register("pin", {
                required: "Transaction PIN is required",
                pattern: {
                  value: /^\d{6}$/,
                  message: "PIN must contain exactly 6 digits",
                },
              })}
              placeholder="••••••"
              className={`${INPUT_CLASS} tracking-[0.5em] text-center`}
            />
            {errors.pin && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.pin.message}
              </p>
            )}
          </div>
          <div>
            <FieldLabel>Confirm transaction PIN</FieldLabel>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              {...register("confirmPin", {
                required: "Confirm your transaction PIN",
                validate: (value) => value === pin || "PINs do not match",
              })}
              placeholder="••••••"
              className={`${INPUT_CLASS} tracking-[0.5em] text-center`}
            />
            {errors.confirmPin && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.confirmPin.message}
              </p>
            )}
            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
              You will use this PIN to authorize transfers in BluPay and
              WhatsApp. Never share it.
            </p>
          </div>
        </div>

        {/* Email Section Card */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-3">
          <div>
            <FieldLabel>Email</FieldLabel>
            <input
              {...register("email", {
                required: "Email is Required",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Invalid email address",
                },
              })}
              placeholder="ex: jon.smith@email.com"
              className={INPUT_CLASS}
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        {/* Password Section Card */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
          <div className="relative">
            <FieldLabel>Password</FieldLabel>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password", {
                  required: "Password is Required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).+$/,
                    message:
                      "Password must include uppercase, lowercase, number, and special character",
                  },
                })}
                placeholder="*********"
                className={`${INPUT_CLASS} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0A4730] transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.password.message}
              </p>
            )}
          </div>

          <div>
            <FieldLabel>Confirm password</FieldLabel>
            <input
              type={showPassword ? "text" : "password"}
              {...register("confirmPassword", {
                required: "You are required to confirm your password.",
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
              placeholder="*********"
              className={INPUT_CLASS}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>
        </div>

        {/* BACKEND ERROR */}
        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-xs">
            {error}
          </div>
        )}

        {/* Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all shadow-lg active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Creating account..." : "Sign up"}
          </button>
        </div>
      </form>

      <div className="mt-8 text-center text-sm">
        <span className="text-slate-500">Have an account? </span>
        <button
          className="font-bold text-[#0A4730] hover:underline"
          onClick={() => navigate("/login")}
        >
          Login
        </button>
      </div>
    </div>
  );
}
